import {DEFAULT_PAGE_SIZE, parseState, stringifyState} from 'apstra-ui-common';
import {transform, get, isEqual, isEmpty, merge} from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

const DEFAULT_SERIALIZATION_SCHEMA = {
  activePage: 'defaultActivePage',
  pageSize: 'defaultPageSize',
  filters: 'defaultFilters',
  sorting: 'defaultSorting',
};

type UseSearchStringFiltersArguments<T extends object> = {
  defaultActivePage?: number,
  defaultPageSize?: number,
  defaultFilters?: T,
  stateQueryParam?: string | null,
  setUserStoreProps?: (props: object) => void,
  defaultSorting?: object,
  defaultSelection?: object,
  serializationSchema?: object,
  pushToHistory?: boolean,
};

export type UseSearchStringFiltersResult<T> = {
  activePage: number,
  pageSize: number,
  updatePagination: (pagination: {activePage: number, pageSize: number}) => void,
  filters: T,
  updateFilters: (filters: T, options?: {resetActivePage?: boolean, resetSelection?: boolean}) => void,
  sorting: object,
  updateSorting: (sorting: object) => void,
  selection: object,
  updateSelection: (selection: object) => void,
};

type SearchStringState<T> = {
  activePage: number,
  pageSize: number,
  filters: T,
  sorting: object,
};

export const useSearchStringFilters = <T extends object>({
  defaultActivePage = 1,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  defaultFilters: defaultFiltersFromProps = {} as T,
  defaultSorting = {},
  defaultSelection = {},
  serializationSchema = DEFAULT_SERIALIZATION_SCHEMA,
  setUserStoreProps,
  stateQueryParam,
  pushToHistory,
}: UseSearchStringFiltersArguments<T>): UseSearchStringFiltersResult<T> => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState(defaultActivePage);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [defaultFilters, setDefaultFilters] = useState(defaultFiltersFromProps);
  const [filters, setFilters] = useState<T>(defaultFiltersFromProps as T);
  const [sorting, setSorting] = useState(defaultSorting);
  const [selection, setSelection] = useState(defaultSelection);
  const searchRef = useRef('');

  const updatePagination = useCallback(({activePage, pageSize}) => {
    setActivePage(activePage);
    setPageSize(pageSize);
    setUserStoreProps?.({pageSize});
  }, [setUserStoreProps]);

  const updateFilters = useCallback((newFilters, {resetActivePage = true, resetSelection = true} = {}) => {
    if (!isEqual(filters, newFilters)) {
      setFilters(newFilters);
      setActivePage(resetActivePage ? defaultActivePage : activePage);
      setSelection(resetSelection ? defaultSelection : selection);
    }
  }, [filters, defaultActivePage, activePage, defaultSelection, selection]);

  const updateSorting = useCallback((newSorting) => {
    if (!isEqual(sorting, newSorting)) {
      setSorting(newSorting);
    }
  }, [sorting]);

  const deserializeState = (stringifiedSerializedState: string | null): SearchStringState<T> => {
    let serializedState = null;
    try {
      serializedState = parseState(stringifiedSerializedState);
    } catch {}
    const defaultValuesFromProps = {
      defaultActivePage, defaultPageSize, defaultFilters, defaultSorting
    };
    return transform(serializationSchema, (result, defaultPropName, name) => {
      const value = get(serializedState, [name], null);
      const defaultValue = defaultValuesFromProps[defaultPropName];
      result[name] = value !== null && typeof value === typeof defaultValue ? value : defaultValue;
    }, {} as SearchStringState<T>);
  };

  const newSearch = stateQueryParam ? location.search : '';
  useEffect(() => {
    if (stateQueryParam) {
      if (newSearch !== searchRef.current) {
        searchRef.current = newSearch;
        const searchParams = new URLSearchParams(newSearch);
        const deserializedState = deserializeState(searchParams.get(stateQueryParam));
        updateFilters(merge({}, filters, deserializedState.filters));
        updatePagination({activePage: deserializedState.activePage, pageSize: deserializedState.pageSize});
        updateSorting(deserializedState.sorting);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newSearch]);

  const currentState = useMemo(() => {
    const currentValues = {
      activePage, pageSize, filters, sorting
    };
    const defaultValuesFromProps = {
      defaultActivePage, defaultPageSize, defaultFilters, defaultSorting
    };
    const serializedState = transform(serializationSchema, (result, defaultPropName, name) => {
      const value = currentValues[name];
      if (!isEqual(value, defaultValuesFromProps[defaultPropName])) {
        result[name] = value;
      }
    }, {});
    return isEmpty(serializedState) ? '' : stringifyState(serializedState);
  }, [
    activePage, defaultActivePage, defaultFilters, defaultPageSize, defaultSorting,
    filters, pageSize, serializationSchema, sorting
  ]);

  const shouldSkipStateChangeEffectOnMountRef = useRef(true);
  useEffect(() => {
    if (shouldSkipStateChangeEffectOnMountRef.current) {
      shouldSkipStateChangeEffectOnMountRef.current = false;
      return;
    }
    if (stateQueryParam) {
      const searchParams = new URLSearchParams(location.search);
      if (currentState) {
        searchParams.set(stateQueryParam, currentState);
      } else {
        searchParams.delete(stateQueryParam);
      }
      const searchParamsAsString = searchParams.toString();
      const search = searchParamsAsString ? '?' + searchParamsAsString : '';
      if (search !== searchRef.current) {
        searchRef.current = search;
        navigate({search}, {replace: !pushToHistory});
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentState]);

  useEffect(() => {
    if (!isEqual(defaultFilters, defaultFiltersFromProps)) {
      updateFilters(defaultFiltersFromProps);
      setDefaultFilters(defaultFiltersFromProps);
    }
  }, [defaultFilters, defaultFiltersFromProps, updateFilters]);

  const updateSelection = useCallback((newSelection) => {
    if (!isEqual(selection, newSelection)) {
      setSelection(newSelection);
    }
  }, [selection, setSelection]);

  return {
    activePage, pageSize, filters, sorting,
    updatePagination, updateFilters, updateSorting,
    selection, updateSelection
  };
};
